import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ChangeDetectorRef } from '@angular/core';
import { VideoServerService } from "app/services/videoserver.service";
import { CookieService } from "ngx-cookie-service";
import moment from "moment-timezone";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { DateAdapter } from "@angular/material/core";
import { CameraVisitIconPipe, FunctionPipe, MomentPipe } from "../../pipes/pipes";
import { RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { NgClass, CommonModule } from "@angular/common";
import { ApiService } from "app/services/api.service";
import { IMultiSelectOption, NgxBootstrapMultiselectModule } from "ngx-bootstrap-multiselect";
import { CameraVisitLocationNamePipe } from "app/components/camera-supervision/camera-supervision-pipes";
import { FilterOptions, CameraSupervisionFilter, HelperService, UiLang } from "app/services/helper.service";
import { ActiveCameraVisit, CameraVisit } from "app/models/camerasupervision.model";
import { Observable, forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { MyDateAdapter } from "app/shared/my-date-adapter.shared";
import { WEEKDAY_OPTIONS } from "./create-new-visit/create-new-visit.component";
import { formatTime, getVisitDurationTimes } from "app/services/camera-schedule.service";
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { CanPipe } from 'app/pipes/can.pipe';
import { Areas } from 'app/models/permission.model';

/**
 * Helpers for the CameraSupervisionComponent
 */

const MINUTES_IN_DAY = 24 * 60;
const MINUTES_IN_WEEK = 7 * MINUTES_IN_DAY;

/**
 * Returns a timestamp representing the start of the LOCAL calendar day for a given date.
 */
const getLocalDayTimestamp = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

/**
 * Returns minutes since midnight for a visit, using a source appropriate to each visit type.
 */
const getMinutesSinceMidnight = (visit: CameraVisit): number => {
  if (visit.isNightowl) {
    return (visit.time?.hour ?? 0) * 60 + (visit.time?.minute ?? 0);
  }
  const d = new Date(visit.startDate);
  return d.getHours() * 60 + d.getMinutes();
};

const sortVisitsByStartTime = (visitA: CameraVisit, visitB: CameraVisit): number => {
  const aDayTs = getLocalDayTimestamp(new Date(visitA.startDate));
  const bDayTs = getLocalDayTimestamp(new Date(visitB.startDate));

  // Different calendar days: sort chronologically by day
  if (aDayTs !== bDayTs) return aDayTs - bDayTs;

  // Same calendar day: compare time-of-day using per-type local time derivation
  return getMinutesSinceMidnight(visitA) - getMinutesSinceMidnight(visitB);
}

const sortByNameDesc = (a: any, b: any) => {
  return a.name > b.name;
}

interface IWeekday {
  title: string;
  isChecked: boolean;
  value: number;
}

@Component({
    selector: "camera-supervision",
    templateUrl: "./camera-supervision.component.html",
    styleUrls: ["./camera-supervision.component.css"],
    imports: [CanPipe, 
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatButtonModule,
    RouterLink,
    NgClass,
    TranslateModule,
    NgxBootstrapMultiselectModule,
    FunctionPipe,
    CameraVisitLocationNamePipe,
    CameraVisitIconPipe
],
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [
        { provide: DateAdapter, useClass: MyDateAdapter },
    ]
})
export class CameraSupervisionComponent implements OnInit {
  readonly Areas = Areas;

  constructor(
    private videoServer: VideoServerService,
    private api: ApiService,
    private cookies: CookieService,
    private translate: TranslateService,
    private dateAdapter: DateAdapter<any>,
    private helper: HelperService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar
  ) { }

  user: any;
  allVisits: CameraVisit[];
  allVisitsOriginal: CameraVisit[];
  futureVisits: CameraVisit[];
  activeVisits: ActiveCameraVisit[];
  filterMinDate: Date = moment().toDate();
  filterMaxDate: Date = moment().add(6, "d").toDate();
  locale: string;

  userTimezoneOffsetHours = -new Date().getTimezoneOffset() / 60;

  editingVisitId: string | null = null;
  editingVisitData: { startTime: string; duration: number; gracePeriod: number } | null = null;
  editSavingInProgress: boolean = false;

  // Date filters
  filterStartDay: Date = moment().toDate();
  filterEndDay: Date = moment().add(6, "d").toDate();

  // Time filters
  filterStartTime: string = "00:00";
  filterEndTime: string = "23:59";

  advancedFilter: CameraSupervisionFilter = {
    settings: {
      enableSearch: true,
      showUncheckAll: true,
      dynamicTitleMaxItems: 0,
      checkedStyle: "fontawesome",
      containerClasses: "fullWidth",
      buttonClasses: "btn btn-outline-dark fullWidth",
    },
    location: new FilterOptions(),
    cameraGroup: new FilterOptions(),
    receiverGroup: new FilterOptions(),
    alertRoute: new FilterOptions(),
  };

  weekDayFilterList: IWeekday[] = [
    { title: "MONDAY_SHORT", isChecked: false, value: 1 },
    { title: "TUESDAY_SHORT", isChecked: false, value: 2 },
    { title: "WEDNESDAY_SHORT", isChecked: false, value: 3 },
    { title: "THURSDAY_SHORT", isChecked: false, value: 4 },
    { title: "FRIDAY_SHORT", isChecked: false, value: 5 },
    { title: "SATURDAY_SHORT", isChecked: false, value: 6 },
    { title: "SUNDAY_SHORT", isChecked: false, value: 7 },
  ];

  /**
   * Parse advanced filter options
   */
  parseFilterOptionsFromVisits(visits: CameraVisit[]): void {
    const cameraGroupOptions = this.parseCameraGroupOptions(visits);

    // Empty the filter list temporarily to update option list
    this.advancedFilter.cameraGroup.filterList = [];
    this.advancedFilter.cameraGroup.filterList = cameraGroupOptions;
  }

  /**
   * Parse camera group filter options from visit list
   */
  parseCameraGroupOptions(visits: CameraVisit[]): IMultiSelectOption[] {
    const parsedList = [];
    visits.forEach((visit?) => {
      const exists = parsedList.some((obj) => obj.id === visit.cameraGroupId);
      if (!exists && visit) {
        parsedList.push({
          id: visit.cameraGroupId,
          name: visit.cameraGroupName,
        });
      }
    });
    return parsedList.sort((a, b) => {
      return a.name > b.name ? 1 : a.name < b.name ? -1 : 0;
    });
  }

  getAdvancedFilterTranslations(): void {
    const commonTexts = {
      checkAll: this.translate.instant("FILTER_CHECK_ALL"),
      uncheckAll: this.translate.instant("FILTER_UNCHECK_ALL"),
      checked: this.translate.instant("FILTER_CHECKED"),
      checkedPlural: this.translate.instant("FILTER_CHECKED"),
      searchPlaceholder: this.translate.instant("SEARCH"),
      allSelected: this.translate.instant("FILTER_ALL_SELECTED"),
    };

    this.advancedFilter.alertRoute.texts = Object.assign(
      {},
      commonTexts,
      { defaultTitle: this.translate.instant("ALARM_ROUTING") }
    );
    this.advancedFilter.location.texts = Object.assign(
      {},
      commonTexts,
      { defaultTitle: this.translate.instant("LOCATION") }
    );
    this.advancedFilter.cameraGroup.texts = Object.assign(
      {},
      commonTexts,
      { defaultTitle: this.translate.instant("CAMERA_GROUP_NAME") }
    );
    this.advancedFilter.receiverGroup.texts = Object.assign(
      {},
      commonTexts,
      { defaultTitle: this.translate.instant("RECEIVER_GROUP") }
    );
  }

  /** Filter options, or null when they can't be loaded: the visits still load, only the filters stay empty */
  getCameraVisitAdvancedFilters(): Observable<any> {
    return this.api.getReportFilteringOptions().pipe(catchError(() => of(null)));
  }

  // Wrapping needed, since we call this in the template
  getVisitDurationTimes(visit: CameraVisit): string {
    return getVisitDurationTimes(visit);
  }

  getUserGroupName(groupId: string, list: IMultiSelectOption[]): string {
    let name = groupId;
    const listItem = list.find(x => x.id === groupId);
    if (listItem) {
      name = listItem.name;
    }
    return name;
  }

  getLocationId(serverIdLocationId: string): string {
    return serverIdLocationId.slice(-8);
  }

  mergeCameraVisits(): any {
    this.addVisitsThatEndedToday();
    // Make deep copy from future visits
    this.allVisits = JSON.parse(JSON.stringify(this.futureVisits));
    this.activeVisits.forEach((e) => {
      const visitData: CameraVisit = e.scheduleItem;
      visitData.activeVisit = true;
      visitData.visitType = e.visitType;
      this.allVisits.push(visitData);
    });
    this.allVisits = this.removeTempVisits(this.allVisits).sort(sortVisitsByStartTime);
    this.allVisitsOriginal = JSON.parse(JSON.stringify(this.allVisits));
  }

  clearFilters(): void {
    this.filterStartDay = moment().toDate();
    this.filterEndDay = moment().add(6, "d").toDate();
    this.filterStartTime = "00:00";
    this.filterEndTime = "23:59";
    this.weekDayFilterList.forEach((day) => (day.isChecked = false));
    this.advancedFilter.alertRoute.selectedList = [];
    this.advancedFilter.cameraGroup.selectedList = [];
    this.advancedFilter.location.selectedList = [];
    this.advancedFilter.receiverGroup.selectedList = [];
    this.getAllCameraVisits();
  }

  /**
   * Get total minutes of the day time
   * @param {string} time Time string in format HH:mm
   * @returns number of minutes
   */
  convertHhmmToMinutes(time: string): number {
    const [hours, minutes] = time.split(":");
    const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
    return totalMinutes;
  }

  applyFilters(): void {
    let filteredVisits = this.filterTime(this.allVisitsOriginal);
    filteredVisits = this.applyAdvancedFilters(filteredVisits);
    filteredVisits = this.filterWeekDay(filteredVisits);
    filteredVisits = this.filterDate(filteredVisits);
    this.allVisits = filteredVisits;
  }

  applyAdvancedFilters(cameraVisits: CameraVisit[]): CameraVisit[] {
    const locations = this.advancedFilter.location.selectedList ?? [];
    const cameraGroups = this.advancedFilter.cameraGroup.selectedList ?? [];
    const receiverGroups = this.advancedFilter.receiverGroup.selectedList ?? [];
    //const alertRoutes = this.advancedFilter.alertRoute.selectedList ?? [];

    return cameraVisits.filter((visit) => {
      const matchesLocation = locations.length === 0 || locations.includes(visit.locationId?.slice(-8));
      const matchesCameraGroup = cameraGroups.length === 0 || cameraGroups.includes(visit.cameraGroupId);
      const matchesReceiverGroup = receiverGroups.length === 0 ||
        receiverGroups.some(id => visit.userGroups?.some(groupId => groupId === id));

      return matchesLocation && matchesCameraGroup && matchesReceiverGroup;
    });
  }

  filterTime(cameraVisits: CameraVisit[]): CameraVisit[] {
    const filterStartMinutes = this.convertHhmmToMinutes(this.filterStartTime);
    const filterEndMinutes = this.convertHhmmToMinutes(this.filterEndTime);

    // Don't filter visits if start time is 00:00 and end time is 23:59
    if (filterStartMinutes === 0 && filterEndMinutes === 1439) {
      return cameraVisits;
    }

    return cameraVisits.filter(visit => {
      const visitStartTimeString = formatTime(moment(visit.startDate).format("kk:mm"));
      const visitEndTimeString = formatTime(moment(visit.startDate).add(visit.duration, "minutes").format("kk:mm"));

      const visitStartMinutes = this.convertHhmmToMinutes(visitStartTimeString);
      const visitEndMinutes = this.convertHhmmToMinutes(visitEndTimeString);

      return this.isWithinFilterRange(visitStartMinutes, visitEndMinutes, filterStartMinutes, filterEndMinutes);
    });
  }

  onDateChanged(): void {

    this.futureVisits = this.allVisitsOriginal;

    this.mergeCameraVisits();

    // Filter data based on the selected UI filters
    this.applyFilters();

    this.parseFilterOptionsFromVisits(this.allVisits);


  }

  filterWeekDay(cameraVisits: CameraVisit[]): CameraVisit[] {
    const checkedDays = this.weekDayFilterList
      .filter((day) => day.isChecked)
      .map((day) => day.value);

    if (checkedDays.length > 0) {
      cameraVisits = cameraVisits.filter((visit) =>
        checkedDays.includes(visit.time.day)
      );
    }
    return cameraVisits;
  }

  filterDate(cameraVisits: CameraVisit[]): CameraVisit[] {
    const startDate = moment(this.filterStartDay).startOf("day").toDate();
    const endDate = moment(this.filterEndDay).endOf("day").toDate();
    cameraVisits = cameraVisits ? cameraVisits.filter((visit) => {
      return (
        new Date(visit.startDate) >= startDate &&
        new Date(visit.startDate) <= endDate
      );
    }) : cameraVisits;
    return cameraVisits;
  }

  deleteCameraVisit(visitToDelete: CameraVisit): void {
    if (visitToDelete?.isNightowl) {
      this.api.deleteNightowlVisit(visitToDelete.id).subscribe({
        next: () => {  
          this.allVisits = this.allVisits.filter((visit) => visit.id !== visitToDelete.id); 
          this.logCameraVisitDeletion(visitToDelete);
        },
        error: () => {
        this.snackBar.open(
          this.translate.instant('VISIT_DELETE_ERROR'),
          undefined,
          { duration: 4000, panelClass: ['snackbar-error'] }
        );
        }
      });
    }
    else {
      this.videoServer
        .deleteVisit(visitToDelete.cameraGroupId, visitToDelete.id)
        .subscribe(() => {
          this.allVisits = this.allVisits.filter((visit) => visit.id !== visitToDelete.id);
          // Remove visit from original list as well, otherwise it will be shown again when filters are applied
          this.allVisitsOriginal = this.allVisitsOriginal.filter((visit) => visit.id !== visitToDelete.id);
        });
    }
  }

  logCameraVisitDeletion(visit: CameraVisit): void {
    const timeSpanMmHh = this.getVisitDurationTimes(visit);

    const weekdays = WEEKDAY_OPTIONS
      .filter((day) => {
        return day.id === visit.time.day;
      })
      .map((day) => day.name);

    this.api.logScheduledVisitActivities([
      {
        cameraGroupId: visit.cameraGroupId,
        cameraGroupName: visit.cameraGroupName,
        action: "Delete",
        changeRecord: {
          weekdayTranslationCodes: weekdays,
          startTime: timeSpanMmHh.split("-")[0]?.trim(),
          endTime: timeSpanMmHh.split("-")[1]?.trim(),
          extendedVisitTimeMinutes: visit.gracePeriod,
        }
      }
    ]).subscribe();
  }

  startEditVisit(visit: CameraVisit): void {
    const startTime = visit.isNightowl
      ? `${String(visit.time.hour).padStart(2, '0')}:${String(visit.time.minute).padStart(2, '0')}`
      : moment(visit.startDate).format('HH:mm');
    this.editingVisitId = visit.id;
    this.editingVisitData = {
      startTime,
      duration: visit.duration,
      gracePeriod: visit.gracePeriod ?? 0
    };
  }

  cancelEditVisit(): void {
    this.editingVisitId = null;
    this.editingVisitData = null;
  }

  /**
   * Returns true if the current edit values overlap with any other visit
   * in the same camera group. A visit's grace period counts as part of its
   * time range (nightowl visits have no grace period). Ranges are compared
   * as week-minutes so visits running past midnight (including Sunday into
   * Monday) are checked against the following day's visits.
   */
  checkEditOverlap(visit: CameraVisit): boolean {
    if (!this.editingVisitData?.startTime || !this.editingVisitData.duration) return false;

    const [editHour, editMinute] = this.editingVisitData.startTime.split(':').map(Number);
    const editStart = this.toWeekMinute(visit.time?.day, editHour * 60 + editMinute);
    const editGrace = visit.isNightowl ? 0 : Number(this.editingVisitData.gracePeriod) || 0;
    const editLength = Number(this.editingVisitData.duration) + editGrace;

    const candidates = this.allVisitsOriginal.filter(v =>
      v.id !== visit.id &&
      v.cameraGroupId === visit.cameraGroupId
    );

    return candidates.some(v => {
      const vMinuteOfDay = v.isNightowl
        ? v.time.hour * 60 + v.time.minute
        : new Date(v.startDate).getHours() * 60 + new Date(v.startDate).getMinutes();
      const vStart = this.toWeekMinute(v.time?.day, vMinuteOfDay);
      const vGrace = v.isNightowl ? 0 : v.gracePeriod ?? 0;
      const vLength = v.duration + vGrace;
      // Also compare against the other visit shifted a week back/forward to handle wraparound
      return [-MINUTES_IN_WEEK, 0, MINUTES_IN_WEEK].some(shift =>
        editStart < vStart + shift + vLength && editStart + editLength > vStart + shift
      );
    });
  }

  /** Converts a 1..7 (Monday..Sunday) weekday and minute of day into minutes from Monday 00:00. */
  private toWeekMinute(day: number, minuteOfDay: number): number {
    return (day - 1) * MINUTES_IN_DAY + minuteOfDay;
  }

  /** Returns a translation key if duration is out of range, otherwise null. */
  getDurationError(visit: CameraVisit): string | null {
    const d = Number(this.editingVisitData?.duration);
    if (!d || d < 1) return 'VISIT_DURATION_WARNING';
    if (visit.isNightowl && d > 30) return 'VISIT_DURATION_WARNING';
    return null;
  }

  /** Returns a translation key if grace period is out of range, otherwise null. */
  getGracePeriodError(): string | null {
    const g = Number(this.editingVisitData?.gracePeriod);
    if (g < 0) return 'VISIT_GRACE_PERIOD_WARNING';
    return null;
  }

  /** Returns true if any field value is outside its allowed range. */
  hasEditRangeError(visit: CameraVisit): boolean {
    return this.getDurationError(visit) !== null ||
      (!visit.isNightowl && this.getGracePeriodError() !== null);
  }

  submitEditVisit(visit: CameraVisit): void {
    if (!this.editingVisitData) return;
    this.editSavingInProgress = true;

    const oldStartTime = visit.isNightowl
      ? `${String(visit.time.hour).padStart(2, '0')}:${String(visit.time.minute).padStart(2, '0')}:00`
      : moment(visit.startDate).format('HH:mm') + ':00';

    const lyraRequestData = {
      visitScheduleId: visit.id,
      startTime: this.editingVisitData.startTime + ':00',
      duration: this.editingVisitData.duration,
      gracePeriod: visit.isNightowl ? null : this.editingVisitData.gracePeriod,
      deviceType: visit.isNightowl ? 'Nightowl' : 'Camera',
      cameraGroupId: visit.cameraGroupId,
      cameraGroupName: visit.cameraGroupName,
      day: visit.time.day,
      oldStartTime,
      oldDuration: visit.duration,
      oldGracePeriod: visit.isNightowl ? null : (visit.gracePeriod ?? 0),
    };

    const onSuccess = () => {
      this.editSavingInProgress = false;
      this.cancelEditVisit();
      this.getAllCameraVisits();
      this.snackBar.open(
        this.translate.instant('UPDATE_SUCCESS'),
        undefined,
        { duration: 3000, panelClass: ['snackbar-success'] }
      );
    };

    const onError = (err: HttpErrorResponse) => {
      console.error('Failed to edit scheduled visit:', err);
      this.editSavingInProgress = false;
      this.snackBar.open(
        this.translate.instant('VISIT_EDIT_ERROR'),
        undefined,
        { duration: 4000, panelClass: ['snackbar-error'] }
      );
    };

    if (visit.isNightowl) {
      this.api.editScheduledVisit(lyraRequestData).subscribe({ next: onSuccess, error: onError });
    } else {
      const [hour, minute] = this.editingVisitData.startTime.split(':').map(Number);
      const videoserverData = {
        time: { hour, minute },
        duration: Number(this.editingVisitData.duration),
        gracePeriod: Number(this.editingVisitData.gracePeriod),
      };
      this.videoServer.editVisit(visit.cameraGroupId, visit.id, videoserverData).subscribe({
        next: () => {
          this.api.editScheduledVisit(lyraRequestData).subscribe({ next: onSuccess, error: onError });
        },
        error: onError,
      });
    }
  }

  addVisitsThatEndedToday(): void {
    const vc = this.futureVisits.length;
    for (let i = 0; i < vc; i++) {
      if (new Date(this.futureVisits[i].startDate).getDate() == moment().add(7, "d").toDate().getDate()) {
        if (
          this.futureVisits[i].stopTime.hour < new Date().getHours() ||
          (this.futureVisits[i].stopTime.hour == new Date().getHours() &&
          this.futureVisits[i].stopTime.minute < new Date().getMinutes())
        ) {
          this.futureVisits[i].startDate = new Date(
            new Date(this.futureVisits[i].startDate).setDate(
              new Date(this.futureVisits[i].startDate).getDate() - 7
            )
          );
        }
      }
    }
  }

  getAllCameraVisits(): void {
    forkJoin({
    futureVisits: this.videoServer.getAllCameraVisits("10079"),
    nightowlVisits: this.api.getNightowlVisits(),
    activeVisits: this.videoServer.getActiveCameraVisits()
    }).subscribe(({ futureVisits, nightowlVisits, activeVisits }) => {
      // Merge and tag all future visits
      this.futureVisits = [
        ...(futureVisits || []).map((x: any) => ({
          ...x,
          visitType: "TYPE_SCHEDULED_VISIT",
        })),
        ...(nightowlVisits || []).map((x: any) => {

          return {
            ...x,
            visitType: "TYPE_SCHEDULED_VISIT",
            isNightowl: true,
            time: this.toLocalTime(x.time, this.userTimezoneOffsetHours),
            stopTime: this.toLocalTime(x.stopTime, this.userTimezoneOffsetHours),
          };
        }),
      ],

      this.activeVisits = (activeVisits || []).map((x: any) => ({
        ...x,
        visitType: "TYPE_SCHEDULED_VISIT",
      }));

      this.cdr.detectChanges();
      this.mergeCameraVisits();
      this.parseFilterOptionsFromVisits(this.allVisitsOriginal);
      this.applyFilters();
      this.cdr.detectChanges(); 
    });
  }

  getWeekday(visit: CameraVisit): string {
    const scheduleDay = visit?.time?.day;

    // Nightowl schedules use 1..7 for Monday..Sunday.
    // Format a fixed UTC Monday reference date so browser-local timezone doesn't shift the weekday label.
    if (Number.isInteger(scheduleDay) && scheduleDay >= 1 && scheduleDay <= 7) {
      const referenceDate = new Date(Date.UTC(2024, 0, scheduleDay)); // 2024-01-01 is a Monday
      return referenceDate.toLocaleDateString(this.locale, { weekday: 'long', timeZone: 'UTC' });
    }

    const date = new Date(visit?.startDate);
    return date.toLocaleDateString(this.locale, { weekday: 'long' });
  }

  toLocalTime(time: { day: number, hour: number, minute: number, timeZone?: string | null }, offsetHours: number) {
    const hasLocalizedTimes = time?.timeZone != null && time.timeZone.toUpperCase() !== 'UTC';

    if (hasLocalizedTimes) {
      return {
        day: time.day,
        hour: time.hour,
        minute: time.minute,
        weekminute: (time.day - 1) * 24 * 60 + time.hour * 60 + time.minute,
        timeZone: time.timeZone
      };
    }

    let localHour = time.hour + offsetHours;
    let localDay = time.day;
    // Handle hour overflow/underflow
    if (localHour >= 24) {
      localHour -= 24;
      localDay = localDay + 1;
      if (localDay > 7) localDay = 1; // wrap to Sunday
    } else if (localHour < 0) {
      localHour += 24;
      localDay = localDay - 1;
      if (localDay < 1) localDay = 7; // wrap to Saturday
    }

    const weekminute = (localDay - 1) * 24 * 60 + localHour * 60 + time.minute;
    return {
      day: localDay,
      hour: localHour,
      minute: time.minute,
      weekminute
    };
  }

  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem("user"));
    const language = localStorage.getItem("language") as UiLang;
    this.helper.setDateAdapterLocale(language, this.dateAdapter);
    this.getAdvancedFilterTranslations();

    // Set a default locale if currentLang is not defined
    const extractedLocale = this.translate.currentLang
      ? this.translate.currentLang.split('-')[0]
      : 'en';  // default locale
    this.locale = extractedLocale && extractedLocale.trim() ? extractedLocale : 'en';

    // Use 'en-gb' for English language to use 24h format
    if (this.locale === "en") this.locale = "en-gb";

    this.getCameraVisitAdvancedFilters().subscribe((res) => {
      // Make login first to video server if token not found
      if (!this.cookies.get("videoserver-session-token")) {
        this.videoServer.login().subscribe((res) => {
          // Set Video server API token into cookies
          this.cookies.set(
            "videoserver-session-token",
            "Bearer " + res.token,
            undefined,
            "/",
            undefined,
            false,
            "Lax"
          );
          this.getAllCameraVisits();
        });
      } else {
        this.getAllCameraVisits();
      }
      this.advancedFilter.alertRoute.filterList = (res?.alertRoutes ?? []).sort(sortByNameDesc);
      this.advancedFilter.location.filterList = (res?.locations ?? []).sort(sortByNameDesc);
      this.advancedFilter.receiverGroup.filterList = (res?.receiverGroups ?? []).sort(sortByNameDesc);

      // Get customer information
      const jsonString = localStorage.getItem("customerInfo");

      if (jsonString) {
        const customerInfo = JSON.parse(jsonString);
        // Add customer to location filters
        this.advancedFilter.location.filterList.unshift({
          id: customerInfo.customerId,
          name: customerInfo.name + " (" + customerInfo.customerId + ")",
        })
      }
    });
  }

  private isWithinFilterRange(visitStartMinutes: number, visitEndMinutes: number, filterStartMinutes: number, filterEndMinutes: number): boolean {
    // Handle case where the filter range spans over midnight
    if (filterEndMinutes < filterStartMinutes) {
      return (
        (visitStartMinutes === 0 || visitStartMinutes >= filterStartMinutes) &&
        visitEndMinutes <= filterEndMinutes
      );
    }

    // Return false if the visit end time is before the start time, as it should'b be shown since it's not within the filter range
    if (visitEndMinutes < visitStartMinutes) {
      return false;
    }

    // Otherwise, check if the visit is within the filter range
    return (
      visitStartMinutes >= filterStartMinutes && visitEndMinutes <= filterEndMinutes
    );
  }

  private removeTempVisits(visits: CameraVisit[]): CameraVisit[] {
    return visits.filter((visit) => visit?.snoozedDeleteWhenDone === false);
  }
}
